import React, { useState, useEffect, useCallback } from 'react';
import Toast from '../components/Toast';
import { getRecentFoods, getFrequentFoods, addFoodLog } from '../utils/nutritionApi';

function QuickAdd() {
  const [tab, setTab] = useState('recent');
  const [recentFoods, setRecentFoods] = useState([]);
  const [frequentFoods, setFrequentFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFood, setSelectedFood] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [mealType, setMealType] = useState('snack');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [adding, setAdding] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [recent, frequent] = await Promise.all([
        getRecentFoods(),
        getFrequentFoods()
      ]);
      setRecentFoods(recent);
      setFrequentFoods(frequent);
    } catch (err) {
      console.error('Failed to load foods:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectFood = (food) => {
    setSelectedFood(food);
    setQuantity(1);
  };

  const handleClosePanel = () => {
    setSelectedFood(null);
  };

  const adjustQuantity = (delta) => {
    setQuantity(prev => {
      const next = Math.round((prev + delta) * 10) / 10;
      return next < 0.1 ? 0.1 : next;
    });
  };

  const calc = (val) => val != null ? Math.round(val * quantity * 10) / 10 : null;

  const handleAddToLog = async () => {
    if (!selectedFood) return;
    setAdding(true);
    try {
      await addFoodLog({
        food_name: selectedFood.food_name,
        calories: selectedFood.calories || 0,
        protein: selectedFood.protein || null,
        carbs: selectedFood.carbs || null,
        fat: selectedFood.fat || null,
        serving_size: selectedFood.serving_size || null,
        serving_unit: selectedFood.serving_unit || null,
        quantity,
        meal_type: mealType,
        date,
        api_food_id: selectedFood.api_food_id || null
      });
      setToast({ message: `${selectedFood.food_name} added to log`, type: 'success' });
      setSelectedFood(null);
      fetchData();
    } catch (err) {
      console.error('Add failed:', err.message);
      setToast({ message: 'Failed to add food', type: 'error' });
    } finally {
      setAdding(false);
    }
  };

  const foods = tab === 'recent' ? recentFoods : frequentFoods;

  return (
    <div className="page quick-add-page">
      <h2>Quick Add</h2>

      {/* Tabs */}
      <div className="qa-tabs">
        <button
          className={`qa-tab ${tab === 'recent' ? 'active' : ''}`}
          onClick={() => { setTab('recent'); setSelectedFood(null); }}
        >
          Recent
        </button>
        <button
          className={`qa-tab ${tab === 'frequent' ? 'active' : ''}`}
          onClick={() => { setTab('frequent'); setSelectedFood(null); }}
        >
          Frequent
        </button>
      </div>

      {loading && (
        <div className="search-loading">
          <div className="loading-spinner"></div>
        </div>
      )}

      {/* Food List */}
      {!loading && !selectedFood && (
        <div className="qa-food-list">
          {foods.length === 0 ? (
            <div className="qa-empty">
              <p className="text-muted">
                {tab === 'recent'
                  ? 'No foods logged yet. Start logging to see your recent foods here.'
                  : 'Log foods a few times to see your most frequent picks here.'}
              </p>
            </div>
          ) : (
            foods.map((food, i) => (
              <button
                key={`${food.food_name}-${i}`}
                className="qa-food-item"
                onClick={() => handleSelectFood(food)}
              >
                <div className="qa-food-info">
                  <span className="qa-food-name">{food.food_name}</span>
                  <span className="qa-food-meta">
                    {food.serving_size && `${food.serving_size}${food.serving_unit || 'g'}`}
                    {tab === 'frequent' && food.count && (
                      <span className="qa-food-count">logged {food.count} time{food.count !== 1 ? 's' : ''}</span>
                    )}
                  </span>
                </div>
                <div className="qa-food-cal">
                  {food.calories != null ? food.calories : '—'}
                  <small> cal</small>
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {/* Add to Log Panel */}
      {selectedFood && (
        <div className="add-panel">
          <button className="add-panel-close" onClick={handleClosePanel}>{'\u2190'} Back to list</button>

          <div className="add-panel-header">
            <h3>{selectedFood.food_name}</h3>
          </div>

          <div className="add-panel-nutrition">
            <div className="add-panel-cal">
              <span className="add-panel-cal-value">{calc(selectedFood.calories) || 0}</span>
              <span className="add-panel-cal-label">calories</span>
            </div>
            <div className="add-panel-macros">
              {calc(selectedFood.protein) != null && (
                <span className="add-panel-macro">P: {calc(selectedFood.protein)}g</span>
              )}
              {calc(selectedFood.carbs) != null && (
                <span className="add-panel-macro">C: {calc(selectedFood.carbs)}g</span>
              )}
              {calc(selectedFood.fat) != null && (
                <span className="add-panel-macro">F: {calc(selectedFood.fat)}g</span>
              )}
            </div>
          </div>

          {selectedFood.serving_size && (
            <p className="add-panel-serving">
              Serving: {selectedFood.serving_size}{selectedFood.serving_unit || 'g'}
            </p>
          )}

          <div className="add-panel-field">
            <label>Quantity</label>
            <div className="quantity-control">
              <button className="qty-btn" onClick={() => adjustQuantity(-0.5)}>−</button>
              <input
                type="number"
                className="qty-value"
                value={quantity}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!isNaN(v) && v > 0) setQuantity(v);
                }}
                min="0.1"
                step="0.5"
              />
              <button className="qty-btn" onClick={() => adjustQuantity(0.5)}>+</button>
            </div>
          </div>

          <div className="add-panel-field">
            <label>Meal</label>
            <div className="meal-selector">
              {['breakfast', 'lunch', 'dinner', 'snack'].map(type => (
                <button
                  key={type}
                  className={`meal-btn ${mealType === type ? 'active' : ''}`}
                  onClick={() => setMealType(type)}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="add-panel-field">
            <label>Date</label>
            <input
              type="date"
              className="date-input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <button
            className="btn btn-primary btn-full"
            onClick={handleAddToLog}
            disabled={adding}
          >
            {adding ? 'Adding...' : 'Add to Log'}
          </button>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default QuickAdd;
